'use client'

import type { DarkModeType } from '@cloud/shared/ui'
import { useSettings } from '@cloud/shared/ui'
import {
  AlignStartHorizontal,
  AlignStartVertical,
  MoonStar,
  RotateCcw,
  Settings,
  Sun,
  SunMoon,
} from 'lucide-react'
import { useCallback } from 'react'

import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'

export function Customizer() {
  const { settings, updateSettings, resetSettings } = useSettings()

  const handleSetMode = useCallback(
    (modeName: DarkModeType) => {
      updateSettings({ ...settings, darkMode: modeName })
    },
    [settings, updateSettings],
  )

  const handleReset = useCallback(() => {
    resetSettings()
  }, [resetSettings])

  return (
    <Sheet>
      <SheetTrigger className="fixed bottom-10 end-0 z-50" asChild>
        <Button size="icon" className="rounded-e-none shadow-sm" aria-label="Customizer">
          <Settings className="shrink-0 h-4 w-4" />
        </Button>
      </SheetTrigger>
      <SheetPortal>
        <SheetContent className="p-0" side="end">
          <ScrollArea className="h-full p-4">
            <div className="flex flex-1 flex-col space-y-4">
              <SheetHeader>
                <SheetTitle>Customizer</SheetTitle>
                <SheetDescription>Different layout? We got you covered.</SheetDescription>
              </SheetHeader>
              <div className="space-y-1.5">
                <p className="text-sm">Mode</p>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    variant={settings.darkMode === 'light' ? 'secondary' : 'outline'}
                    onClick={() => handleSetMode('light')}
                  >
                    <Sun className="shrink-0 h-4 w-4 me-2" />
                    Light
                  </Button>
                  <Button
                    variant={settings.darkMode === 'dark' ? 'secondary' : 'outline'}
                    onClick={() => handleSetMode('dark')}
                  >
                    <MoonStar className="shrink-0 h-4 w-4 me-2" />
                    Dark
                  </Button>
                  <Button
                    variant={settings.darkMode === 'system' ? 'secondary' : 'outline'}
                    onClick={() => handleSetMode('system')}
                  >
                    <SunMoon className="shrink-0 h-4 w-4 me-2" />
                    System
                  </Button>
                </div>
                <div className="space-y-1.5">
                  <span className="text-sm">Layout</span>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant={settings.layout === 'horizontal' ? 'secondary' : 'outline'}
                      onClick={() =>
                        updateSettings({
                          ...settings,
                          layout: 'horizontal',
                        })
                      }
                    >
                      <AlignStartHorizontal className="shrink-0 h-4 w-4 me-2" />
                      Horizontal
                    </Button>
                    <Button
                      variant={settings.layout === 'vertical' ? 'secondary' : 'outline'}
                      onClick={() =>
                        updateSettings({
                          ...settings,
                          layout: 'vertical',
                        })
                      }
                    >
                      <AlignStartVertical className="shrink-0 h-4 w-4 me-2" />
                      Vertical
                    </Button>
                  </div>
                </div>
              </div>

              <Button variant="outline" className="w-full" onClick={handleReset}>
                <RotateCcw className="shrink-0 h-4 w-4 me-2" />
                Reset
              </Button>
            </div>
          </ScrollArea>
        </SheetContent>
      </SheetPortal>
    </Sheet>
  )
}
